import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';

import {
  useFetchRoles,
  useFetchUsuariosERPList,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-accesos.actions';
import type {
  IRotacionSlot,
  IRotacionSlotPayload,
} from '@/shared/actions/erp/tickets-soporte-cliente/rotacion-roles.actions';

const DIAS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: IRotacionSlotPayload) => void;
  loading?: boolean;
  initial?: IRotacionSlot | null;
}

export default function SlotFormDialog({ open, onClose, onSubmit, loading, initial }: Props) {
  const [diaSemana, setDiaSemana] = useState<number>(1);
  const [horaInicio, setHoraInicio] = useState<string>('08:00');
  const [horaFin, setHoraFin] = useState<string>('12:00');
  const [usuarioId, setUsuarioId] = useState<number | null>(null);
  const [usuarioNombre, setUsuarioNombre] = useState<string>('');
  const [rolId, setRolId] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const { data: rolesRes } = useFetchRoles();
  // Mismo source que el tab "Usuarios y Roles" en /parametrizacion/gestion-accesos:
  // lista paginada de usuarios del ERP (auth_user). 200 por pagina cubre la
  // mayoria de empresas; si crece mas se puede agregar busqueda servidor.
  const { data: usuariosRes, isLoading: loadingUsuarios } = useFetchUsuariosERPList({
    q: search,
    page: 1,
    page_size: 200,
    with_assignments: false,
    enabled: open,
  });

  const roles: any[] = (rolesRes as any)?.data ?? [];
  const _ur = usuariosRes as any;
  const usuarios: any[] =
    _ur?.data?.data || (Array.isArray(_ur?.data) ? _ur.data : []);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setDiaSemana(initial.dia_semana);
      setHoraInicio(initial.hora_inicio?.slice(0, 5) ?? '08:00');
      setHoraFin(initial.hora_fin?.slice(0, 5) ?? '12:00');
      setUsuarioId(initial.usuario_id);
      setUsuarioNombre(initial.usuario_nombre ?? '');
      setRolId(initial.rol_id);
    } else {
      setDiaSemana(1);
      setHoraInicio('08:00');
      setHoraFin('12:00');
      setUsuarioId(null);
      setUsuarioNombre('');
      setRolId('');
    }
  }, [open, initial]);

  const canSubmit = !!usuarioId && !!rolId && horaFin > horaInicio;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      dia_semana: diaSemana,
      hora_inicio: `${horaInicio}:00`,
      hora_fin: `${horaFin}:00`,
      usuario_id: usuarioId!,
      usuario_nombre: usuarioNombre,
      rol_id: rolId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? 'Editar slot' : 'Nuevo slot de rotación'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Día"
              value={diaSemana}
              onChange={(e) => setDiaSemana(Number(e.target.value))}
            >
              {DIAS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="time"
              label="Hora inicio"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="time"
              label="Hora fin"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={horaFin <= horaInicio}
              helperText={horaFin <= horaInicio ? 'Debe ser mayor a hora inicio' : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={usuarios}
              loading={loadingUsuarios}
              getOptionLabel={(u: any) =>
                u?.nombre_completo || u?.username || `Usuario ${u?.id ?? ''}`
              }
              value={usuarios.find((u: any) => u.id === usuarioId) ?? null}
              onChange={(_e, v: any) => {
                setUsuarioId(v?.id ?? null);
                setUsuarioNombre(v?.nombre_completo || v?.username || '');
              }}
              onInputChange={(_e, v) => setSearch(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuario"
                  placeholder={loadingUsuarios ? 'Cargando...' : 'Buscar por nombre o usuario'}
                />
              )}
              isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
              noOptionsText={loadingUsuarios ? 'Cargando...' : 'Sin coincidencias'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Rol que aplica"
              value={rolId}
              onChange={(e) => setRolId(e.target.value)}
            >
              {roles.map((r: any) => (
                <MenuItem key={r.id_rol} value={r.id_rol}>
                  {r.nombre} {r.codigo ? `(${r.codigo})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || loading}>
          {initial ? 'Guardar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
